<script setup>
/**
 * The Team page's own left column.
 *
 * Team has no contextual sidebar panel - the shell renders rail-only for it -
 * so this column carries the section's navigation instead: the AI agents that
 * hand work over, the people who pick it up, and the teams they belong to.
 */
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

defineProps({
  assistants: { type: Array, default: () => [] },
  teammates: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  selectedKey: { type: String, default: '' },
});

const emit = defineEmits(['select', 'invite']);

const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const ROW_CLASS =
  'mx-2 flex h-9 w-[calc(100%-1rem)] items-center gap-2.5 rounded-lg px-2.5 text-start text-sm outline outline-1 outline-transparent focus-visible:outline-2 focus-visible:outline-n-brand';
const IDLE_CLASS = 'text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12';
const ACTIVE_CLASS = 'bg-n-violet-3 font-medium text-n-violet-11';

const assistantRoute = assistantId =>
  accountScopedRoute('captain_assistants_overview_index', { assistantId });
</script>

<template>
  <div class="pb-4 bg-n-background">
    <header class="flex items-center h-14 gap-2 px-4">
      <h2
        class="flex-1 min-w-0 m-0 text-sm font-semibold truncate text-n-slate-12"
      >
        {{ t('TEAM_PAGE.NAV.TITLE') }}
      </h2>
      <button
        type="button"
        :aria-label="t('TEAM_PAGE.NAV.INVITE')"
        class="grid rounded-lg size-7 place-items-center bg-n-brand text-white transition-opacity hover:opacity-90 outline outline-1 outline-transparent focus-visible:outline-2 focus-visible:outline-n-brand"
        @click="emit('invite')"
      >
        <span class="i-lucide-plus size-4" />
      </button>
    </header>

    <nav :aria-label="t('TEAM_PAGE.NAV.LABEL')" class="flex flex-col gap-4">
      <div class="flex flex-col gap-0.5">
        <p
          class="px-4 py-1.5 m-0 text-xs font-semibold tracking-wide uppercase text-n-slate-10"
        >
          {{ t('TEAM_PAGE.NAV.AI_AGENTS') }}
        </p>
        <p
          v-if="!assistants.length"
          class="px-4 py-1.5 m-0 text-sm text-n-slate-10"
        >
          {{ t('TEAM_PAGE.NAV.AI_AGENTS_EMPTY') }}
        </p>
        <RouterLink
          v-for="assistant in assistants"
          :key="assistant.id"
          :to="assistantRoute(assistant.id)"
          :title="assistant.name"
          :class="[ROW_CLASS, IDLE_CLASS]"
        >
          <span
            class="grid rounded-lg size-6 shrink-0 place-items-center bg-n-violet-3 text-n-violet-11"
          >
            <span class="i-lucide-sparkles size-3.5" />
          </span>
          <span class="flex-1 min-w-0 truncate">{{ assistant.name }}</span>
        </RouterLink>
      </div>

      <div class="flex flex-col gap-0.5">
        <button
          type="button"
          :aria-current="selectedKey === 'teammates' ? 'true' : undefined"
          :class="[
            ROW_CLASS,
            selectedKey === 'teammates' ? ACTIVE_CLASS : IDLE_CLASS,
          ]"
          @click="emit('select', { view: 'teammates' })"
        >
          <span
            class="flex-1 min-w-0 text-xs font-semibold tracking-wide uppercase truncate"
          >
            {{ t('TEAM_PAGE.NAV.TEAMMATES') }}
          </span>
          <span class="text-xs shrink-0 tabular-nums text-n-slate-10">
            {{ teammates.length }}
          </span>
        </button>
        <button
          v-for="teammate in teammates"
          :key="teammate.id"
          type="button"
          :title="teammate.name"
          :aria-current="
            selectedKey === `teammate-${teammate.id}` ? 'true' : undefined
          "
          :class="[
            ROW_CLASS,
            selectedKey === `teammate-${teammate.id}`
              ? ACTIVE_CLASS
              : IDLE_CLASS,
          ]"
          @click="emit('select', { view: 'teammate-detail', id: teammate.id })"
        >
          <Avatar
            :name="teammate.name"
            :src="teammate.thumbnail"
            :size="24"
            rounded-full
          />
          <span class="flex-1 min-w-0 truncate">{{ teammate.name }}</span>
        </button>
      </div>

      <div class="flex flex-col gap-0.5">
        <button
          type="button"
          :aria-current="selectedKey === 'teams' ? 'true' : undefined"
          :class="[
            ROW_CLASS,
            selectedKey === 'teams' ? ACTIVE_CLASS : IDLE_CLASS,
          ]"
          @click="emit('select', { view: 'teams' })"
        >
          <span
            class="flex-1 min-w-0 text-xs font-semibold tracking-wide uppercase truncate"
          >
            {{ t('TEAM_PAGE.NAV.TEAMS') }}
          </span>
          <span class="text-xs shrink-0 tabular-nums text-n-slate-10">
            {{ teams.length }}
          </span>
        </button>
        <button
          v-for="team in teams"
          :key="team.id"
          type="button"
          :title="team.name"
          :aria-current="selectedKey === `team-${team.id}` ? 'true' : undefined"
          :class="[
            ROW_CLASS,
            selectedKey === `team-${team.id}` ? ACTIVE_CLASS : IDLE_CLASS,
          ]"
          @click="emit('select', { view: 'team-detail', id: team.id })"
        >
          <span
            class="grid rounded-lg size-6 shrink-0 place-items-center bg-n-alpha-2 text-[10px] font-semibold text-n-slate-11 tabular-nums"
          >
            {{ team.memberCount }}
          </span>
          <span class="flex-1 min-w-0 truncate">{{ team.name }}</span>
        </button>
      </div>
    </nav>
  </div>
</template>
